import { create } from "zustand";

/** A name a mounted component gives one URL segment — an entity's id, usually. */
export interface BreadcrumbTitleRegistration {
	segment: string;
	title: string;
}

interface BreadcrumbsStore {
	/** Every registered name, keyed by the registering component's id. */
	registrations: Record<string, BreadcrumbTitleRegistration>;
	/**
	 * The paths the navigation links to, which are the static paths a crumb may lead to. `null`
	 * until a navigation has published them, and then every crumb links.
	 */
	pagePaths: ReadonlySet<string> | null;
	registerTitle: (registrationId: string, registration: BreadcrumbTitleRegistration) => void;
	unregisterTitle: (registrationId: string) => void;
	setPagePaths: (pagePaths: ReadonlySet<string>) => void;
}

/**
 * What the breadcrumbs know beyond the URL: the names pages give their segments, and the paths a
 * page exists at. Registrations are keyed per component rather than per segment, so two components
 * naming the same segment do not unname it for each other when one of them unmounts.
 */
export const useBreadcrumbsStore = create<BreadcrumbsStore>()((set) => ({
	registrations: {},
	pagePaths: null,
	registerTitle: (registrationId, registration) => {
		set((state) => ({
			registrations: { ...state.registrations, [registrationId]: registration },
		}));
	},
	unregisterTitle: (registrationId) => {
		set((state) => {
			const { [registrationId]: _removed, ...registrations } = state.registrations;
			return { registrations };
		});
	},
	setPagePaths: (pagePaths) => {
		// The navigation publishes on every render; an unchanged set keeps the store's state, so
		// the breadcrumbs do not re-render for it.
		set((state) => (hasSamePaths(state.pagePaths, pagePaths) ? state : { pagePaths }));
	},
}));

function hasSamePaths(current: ReadonlySet<string> | null, next: ReadonlySet<string>) {
	if (!current || current.size !== next.size) return false;
	for (const path of next) {
		if (!current.has(path)) return false;
	}
	return true;
}
